<template>
  <div
    class="admin-layout"
    :class="{ 'is-resizing-sidebar': resizingSidebar }"
    :style="{ '--admin-sidebar-width': `${sidebarWidth}px` }"
  >
    <a class="skip-link" href="#main-content">跳至主要内容</a>

    <button
      v-if="mobileNavOpen"
      class="sidebar-backdrop"
      type="button"
      aria-label="关闭导航"
      @click="mobileNavOpen = false"
    ></button>

    <aside
      id="primary-navigation"
      class="sidebar"
      :class="{ open: mobileNavOpen }"
      aria-label="管理后台主导航"
    >
      <div class="brand">
        <div class="brand-mark" aria-hidden="true">
          <img src="../assets/logo.png" alt="" />
        </div>
        <div class="brand-copy">
          <img
            class="brand-wordmark"
            src="../assets/logo-wordmark.png"
            alt="AutoSquare"
          />
          <span>管理后台</span>
        </div>
        <button
          class="sidebar-close"
          type="button"
          aria-label="关闭导航"
          @click="mobileNavOpen = false"
        >
          <X :size="20" :stroke-width="1.8" aria-hidden="true" />
        </button>
      </div>

      <div class="nav-section">
        <p class="nav-label">工作台</p>
        <nav class="sidebar-nav">
          <router-link
            v-for="item in navigation"
            :key="item.to"
            :to="item.to"
            class="nav-item"
            active-class="active"
          >
            <span class="nav-icon">
              <component :is="item.icon" :size="19" :stroke-width="1.8" aria-hidden="true" />
            </span>
            <span>{{ item.label }}</span>
            <ChevronRight class="nav-arrow" :size="16" :stroke-width="1.8" aria-hidden="true" />
          </router-link>
        </nav>

      </div>

      <div class="sidebar-footer">
        <p v-if="authStore.logoutError" role="alert">{{ authStore.logoutError }}</p>
        <div v-if="authStore.user" class="current-user">
          <UserAvatar
            class="sidebar-user-avatar"
            :src="authStore.user.avatar_url || ''"
            :name="authStore.user.username || '管理员'"
            :size="36"
          />
          <div class="user-copy">
            <p class="username">{{ authStore.user.username }}</p>
            <p class="role">
              <ShieldCheck :size="13" :stroke-width="2" aria-hidden="true" />
              {{ getRoleName(authStore.user.role) }}
            </p>
          </div>
        </div>
        <button class="logout-btn" type="button" @click="handleLogout">
          <LogOut :size="17" :stroke-width="1.9" aria-hidden="true" />
          退出登录
        </button>
      </div>
      <div
        class="sidebar-resizer"
        role="separator"
        aria-label="调整侧边栏宽度"
        aria-orientation="vertical"
        :aria-valuemin="sidebarMinWidth"
        :aria-valuemax="sidebarMaxWidth"
        :aria-valuenow="sidebarWidth"
        tabindex="0"
        @pointerdown="startSidebarResize"
        @keydown="resizeSidebarWithKeyboard"
      ></div>
    </aside>

    <main class="main-content">
      <button
        class="menu-btn"
        type="button"
        aria-controls="primary-navigation"
        :aria-expanded="mobileNavOpen"
        aria-label="打开导航"
        @click="mobileNavOpen = true"
      >
        <Menu :size="21" :stroke-width="1.8" aria-hidden="true" />
      </button>

      <div id="main-content" class="content-body" tabindex="-1">
        <slot></slot>
      </div>
    </main>
  </div>
</template>

<script>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  ChevronRight,
  FileText,
  FolderKanban,
  Gem,
  LogOut,
  Menu,
  PanelsTopLeft,
  ShieldCheck,
  Users,
  Wrench,
  X,
} from '@lucide/vue';
import { useAdminAuthStore } from '../store/adminAuth';
import UserAvatar from '../components/UserAvatar.vue';
import {
  clampSidebarWidth,
  loadSidebarWidth,
  MAX_SIDEBAR_WIDTH,
  MIN_SIDEBAR_WIDTH,
  saveSidebarWidth,
} from './sidebar-width.mjs';

export default {
  name: 'AdminLayout',
  components: { ChevronRight, LogOut, Menu, ShieldCheck, UserAvatar, X },
  setup() {
    const authStore = useAdminAuthStore();
    const route = useRoute();
    const router = useRouter();
    const mobileNavOpen = ref(false);
    const sidebarWidth = ref(loadSidebarWidth(window.localStorage));
    const resizingSidebar = ref(false);
    let dragStartX = 0;
    let dragStartWidth = sidebarWidth.value;
    const navigation = [
      { to: '/users', label: '用户管理', icon: Users },
      { to: '/subscriptions', label: '订阅管理', icon: Gem },
      { to: '/services', label: '服务管理', icon: Wrench },
      { to: '/applications', label: '应用接入', icon: PanelsTopLeft },
      { to: '/login-origins', label: '登录接入管理', icon: ShieldCheck },
      { to: '/login-result-pages', label: '登录完成页管理', icon: FileText },
      { to: '/content', label: '官网运维', icon: FolderKanban },
    ];

    const resizeSidebar = (event) => {
      sidebarWidth.value = clampSidebarWidth(
        dragStartWidth + event.clientX - dragStartX
      );
    };

    const stopSidebarResize = () => {
      if (!resizingSidebar.value) return;
      resizingSidebar.value = false;
      saveSidebarWidth(window.localStorage, sidebarWidth.value);
      window.removeEventListener('pointermove', resizeSidebar);
      window.removeEventListener('pointerup', stopSidebarResize);
      window.removeEventListener('pointercancel', stopSidebarResize);
    };

    const startSidebarResize = (event) => {
      if (event.button !== 0 || window.matchMedia('(max-width: 900px)').matches) return;
      event.preventDefault();
      dragStartX = event.clientX;
      dragStartWidth = sidebarWidth.value;
      resizingSidebar.value = true;
      window.addEventListener('pointermove', resizeSidebar);
      window.addEventListener('pointerup', stopSidebarResize);
      window.addEventListener('pointercancel', stopSidebarResize);
    };

    const resizeSidebarWithKeyboard = (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const delta = event.key === 'ArrowLeft' ? -8 : 8;
      sidebarWidth.value = saveSidebarWidth(
        window.localStorage,
        sidebarWidth.value + delta
      );
    };

    const refreshProfileOnFocus = () => {
      if (!authStore.isAuthenticated) return;
      authStore.fetchProfile().catch((error) => {
        if (error.permissionDenied) {
          router.replace('/unauthorized');
        } else if (!authStore.isAuthenticated) {
          router.replace({ path: '/login', query: { return_to: route.fullPath } });
        }
      });
    };

    onMounted(() => {
      window.addEventListener('focus', refreshProfileOnFocus);
    });
    onBeforeUnmount(() => {
      stopSidebarResize();
      window.removeEventListener('focus', refreshProfileOnFocus);
    });

    watch(
      () => route.fullPath,
      () => {
        mobileNavOpen.value = false;
      }
    );

    const handleLogout = () => {
      authStore.logout().catch(() => {});
    };

    const getRoleName = (role) => {
      const roles = {
        normal: '普通用户',
        admin: '管理员',
        superuser: '超级管理员',
      };
      return roles[role] || role;
    };

    return {
      authStore,
      mobileNavOpen,
      navigation,
      resizingSidebar,
      resizeSidebarWithKeyboard,
      sidebarMaxWidth: MAX_SIDEBAR_WIDTH,
      sidebarMinWidth: MIN_SIDEBAR_WIDTH,
      sidebarWidth,
      startSidebarResize,
      handleLogout,
      getRoleName,
    };
  },
};
</script>

<style scoped>
.admin-layout {
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
}

.skip-link {
  position: fixed;
  top: 12px;
  left: 12px;
  z-index: calc(var(--z-modal) + 1);
  padding: 10px 14px;
  border-radius: var(--radius-sm);
  color: #ffffff;
  background: var(--primary-color);
  transform: translateY(-160%);
  transition: transform var(--transition-fast);
}

.skip-link:focus {
  transform: translateY(0);
}

.sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: var(--z-sidebar);
  display: flex;
  width: var(--admin-sidebar-width);
  flex-direction: column;
  border-right: 1px solid rgba(255, 255, 255, 0.76);
  background: rgba(250, 252, 255, 0.9);
  box-shadow: 10px 0 36px -28px rgba(30, 64, 175, 0.42);
  backdrop-filter: blur(12px) saturate(120%);
  -webkit-backdrop-filter: blur(12px) saturate(120%);
}

.sidebar-resizer {
  position: absolute;
  top: 0;
  right: -5px;
  z-index: 1;
  width: 10px;
  height: 100%;
  outline: 0;
  cursor: col-resize;
}

.sidebar-resizer::after {
  position: absolute;
  top: 0;
  right: 4px;
  width: 2px;
  height: 100%;
  background: rgba(37, 99, 235, 0.45);
  content: "";
  opacity: 0;
  transition: opacity var(--transition-fast);
}

.sidebar-resizer:hover::after,
.sidebar-resizer:focus-visible::after,
.is-resizing-sidebar .sidebar-resizer::after {
  opacity: 1;
}

.is-resizing-sidebar,
.is-resizing-sidebar * {
  cursor: col-resize !important;
  user-select: none;
}

.brand {
  display: flex;
  height: 92px;
  align-items: center;
  gap: 11px;
  padding: 0 22px;
}

.brand-mark {
  display: grid;
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  place-items: center;
  overflow: hidden;
  border: 1px solid rgba(69, 88, 173, 0.12);
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 10px 22px -12px rgba(54, 63, 141, 0.6);
}

.brand-mark img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brand-copy {
  min-width: 0;
}

.brand-wordmark {
  display: block;
  width: 116px;
  max-width: 100%;
  height: auto;
}

.brand-copy span {
  display: block;
  margin-top: 1px;
  color: var(--text-muted);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
}

.sidebar-close {
  display: none;
}

.nav-section {
  flex: 1;
  padding: 18px 14px;
}

.nav-label {
  padding: 0 12px 10px;
  color: var(--text-faint);
  font-size: 0.74rem;
  font-weight: 720;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.sidebar-nav {
  display: grid;
  gap: 6px;
}

.nav-item {
  position: relative;
  display: flex;
  min-height: 48px;
  align-items: center;
  gap: 11px;
  padding: 7px 10px;
  overflow: hidden;
  border: 1px solid transparent;
  border-radius: 13px;
  color: var(--text-secondary);
  font-size: 0.96rem;
  font-weight: 640;
  text-decoration: none;
  transition:
    color var(--transition-fast),
    border-color var(--transition-fast),
    background-color var(--transition-fast),
    box-shadow var(--transition-fast),
    transform var(--transition-fast);
}

.nav-item:hover {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.7);
  transform: translateX(2px);
}

.nav-item.active {
  border-color: rgba(37, 99, 235, 0.13);
  color: #1d4ed8;
  background: linear-gradient(110deg, rgba(219, 234, 254, 0.86), rgba(238, 242, 255, 0.72));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.76), 0 8px 18px -14px rgba(37, 99, 235, 0.72);
}

.nav-item.active::before {
  position: absolute;
  left: 0;
  width: 3px;
  height: 22px;
  border-radius: 0 var(--radius-pill) var(--radius-pill) 0;
  background: linear-gradient(180deg, #3b82f6, #4f46e5);
  content: "";
}

.nav-icon {
  display: grid;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 9px;
  color: var(--text-muted);
  background: rgba(255, 255, 255, 0.54);
  transition: color var(--transition-fast), background-color var(--transition-fast);
}

.nav-item.active .nav-icon {
  color: var(--primary-color);
  background: rgba(255, 255, 255, 0.88);
}

.nav-arrow {
  margin-left: auto;
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity var(--transition-fast), transform var(--transition-fast);
}

.nav-item:hover .nav-arrow,
.nav-item.active .nav-arrow {
  opacity: 0.75;
  transform: translateX(0);
}

.sidebar-footer {
  margin: 0 14px 16px;
  padding: 14px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.58);
  box-shadow: var(--shadow-sm);
}

.current-user {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding-bottom: 13px;
}

.sidebar-user-avatar {
  box-shadow: 0 6px 16px -10px rgba(37, 99, 235, 0.65);
}

.user-copy {
  min-width: 0;
}

.username {
  overflow: hidden;
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 680;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.role {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  color: var(--text-muted);
  font-size: 0.76rem;
  font-weight: 580;
}

.logout-btn {
  display: flex;
  width: 100%;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid rgba(220, 38, 38, 0.13);
  border-radius: 11px;
  color: var(--danger);
  background: rgba(254, 242, 242, 0.62);
  font-size: 0.88rem;
  font-weight: 650;
  transition:
    color var(--transition-fast),
    border-color var(--transition-fast),
    background-color var(--transition-fast),
    transform var(--transition-fast);
}

.logout-btn:hover {
  border-color: rgba(220, 38, 38, 0.24);
  color: var(--danger-hover);
  background: var(--danger-soft);
}

.logout-btn:active {
  transform: scale(0.98);
}

.main-content {
  width: calc(100% - var(--admin-sidebar-width));
  min-width: 0;
  margin-left: var(--admin-sidebar-width);
}

.menu-btn {
  display: none;
}

.content-body {
  width: 100%;
  max-width: 1680px;
  margin: 0;
  padding: 24px clamp(24px, 3vw, 44px) 48px;
}

.sidebar-backdrop {
  display: none;
}

@media (max-width: 900px) {
  .sidebar {
    width: min(280px, calc(100vw - 32px));
    transform: translateX(-104%);
    transition: transform var(--transition-normal);
  }

  .sidebar-resizer {
    display: none;
  }

  .sidebar.open {
    transform: translateX(0);
  }

  .sidebar-close {
    display: grid;
    width: 40px;
    height: 40px;
    margin-left: auto;
    place-items: center;
    border: 0;
    border-radius: 10px;
    color: var(--text-muted);
    background: transparent;
  }

  .sidebar-close:hover {
    color: var(--text-primary);
    background: rgba(15, 23, 42, 0.05);
  }

  .main-content {
    width: 100%;
    margin-left: 0;
  }

  .menu-btn {
    display: grid;
    width: 42px;
    height: 42px;
    margin: 16px 16px 0;
    place-items: center;
    border: 1px solid var(--border-glass);
    border-radius: 11px;
    color: var(--text-secondary);
    background: rgba(255, 255, 255, 0.72);
  }

  .sidebar-backdrop {
    position: fixed;
    inset: 0;
    z-index: calc(var(--z-sidebar) - 1);
    display: block;
    border: 0;
    background: rgba(15, 23, 42, 0.32);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
  }
}

@media (max-width: 640px) {
  .content-body {
    padding: 18px 16px 36px;
  }
}

@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .sidebar {
    background: rgba(250, 252, 255, 0.98);
  }
}
</style>
